import { Box, Divider, Typography, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import { useContext, useEffect, useMemo, useState } from "react";
import { useInterval } from "usehooks-ts";

import ScheduleCalendar from "@/components/Icons/ScheduleCalendar.tsx";
import ScheduledMessageCreateModal from "@/components/ScheduledMessageComposer/ScheduledMessageCreateModal.tsx";
import SimpleModal from "@/components/SimpleModal.tsx";
import WorkflowItem from "@/components/WorkflowItem.tsx";
import { workflowMap } from "@/components/workflowUtils";
import { DataContext } from "@/models/DataProvider";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import {
  broadcastActionSelect,
  broadcastRecipientsList,
  scheduleTriggerSelect,
  workflowItemsSelect,
} from "@/models/repositories";
import { SchedulingContext } from "@/models/SchedulingContextProvider.tsx";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";

export default function Workflows({ newDesign }: { newDesign?: boolean }) {
  const theme = useTheme();
  const { timezone } = useContext(SchedulingContext);
  const { loadWorkspaceWorkflowItems } = useContext(DataContext);
  const { myAccountId } = useContext(MyAccountContext);
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const [date] = useState(new Date(Date.now()).toISOString());
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [confirmClose, setConfirmClose] = useState<boolean>(false);

  const workflowItems =
    useReactiveSelectQuery(workflowItemsSelect, [{ workspaceId }], "WorkflowItems:workflowItemsSelect") ?? [];

  const broadcasts =
    useReactiveSelectQuery(broadcastActionSelect, [{ workspaceId }], "WorkflowItems:broadcastActionSelect") ?? [];

  const scheduleTriggers =
    useReactiveSelectQuery(scheduleTriggerSelect, [{ workspaceId, date }], "WorkflowItems:scheduleTriggerSelect") ?? [];

  const broadcastRecipients =
    useReactiveSelectQuery(broadcastRecipientsList, [{ workspaceId }], "WorkflowItems:broadcastRecipients") ?? [];

  useEffect(() => {
    loadWorkspaceWorkflowItems({ workspaceId, myAccountId });
  }, [workspaceId, myAccountId]);

  const workflows = useMemo(() => {
    return workflowMap({
      timezone,
      workflowItems,
      broadcasts,
      scheduleTriggers,
      broadcastRecipients,
    });
  }, [workflowItems, broadcasts, scheduleTriggers, broadcastRecipients, timezone]);

  /**
   * Poll the server for new events when on this page, since they do not come in via app-sync
   */
  useInterval(() => {
    loadWorkspaceWorkflowItems({ workspaceId, myAccountId });
  }, 10000);

  return (
    <>
      <Box data-test="workflows" sx={{ p: 2, maxWidth: newDesign ? "auto" : "1300px", margin: "0 auto" }}>
        <Box
          sx={{
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography component="h3" sx={{ fontSize: "1.75rem", fontWeight: 700 }}>
              Scheduled Messages
            </Typography>
          </Box>
          <Box>
            <Box>
              <Button sx={{ gap: 1 }} onClick={() => setModalOpen(() => true)} variant="contained">
                <ScheduleCalendar /> <Box>New Scheduled Message</Box>
              </Button>
            </Box>
          </Box>
        </Box>
        <Divider
          sx={{
            width: "100%",
            borderWidth: "0.75px",
            borderColor: theme.palette.secondary.light,
            mt: 3,
            mb: 2,
          }}
        />
        {workflows?.length > 0 ? (
          <>
            <Box
              sx={{
                height: `calc(100vh - ${newDesign ? "120px" : "210px"})`,
                pr: 1,
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  overflowY: "auto",
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(450px, 1fr))",
                  gap: 2,
                }}
              >
                {workflows?.map((item) => (
                  <WorkflowItem workflowItem={item} key={item.id} />
                ))}
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: "center" }}>No scheduled messages</Box>
        )}
      </Box>
      <SimpleModal
        open={modalOpen}
        closeModal={() => {
          if (hasChanges) {
            setConfirmClose(() => true);
            setTimeout(() => {
              setConfirmClose(() => false);
            }, 200);
          } else {
            setModalOpen(() => false);
          }
        }}
        title="Schedule a message"
        sx={{
          position: "relative",
          boxShadow: hasChanges ? `0 0 0 2px ${theme.palette.info.main}` : "",
        }}
      >
        <ScheduledMessageCreateModal
          askToConfirmClose={confirmClose}
          setHasChanges={(value: boolean) => setHasChanges(() => value)}
          closeModal={() => setModalOpen(() => false)}
        />
      </SimpleModal>
    </>
  );
}
